import {
  CartItem,
  GetSpecialtyRestaurantBundleResponse,
  ItineraryResponse,
} from '@msc-b2c/interfaces/types';
import { addDaysAtSea, formatTime } from '@msc-b2c/shared-utils';

const normalizeDate = (value?: string): string => {
  if (!value) return '';
  const trimmed = value.trim();
  if (!trimmed) return '';

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return trimmed;
  }

  if (trimmed.includes('T')) {
    return trimmed.split('T')[0];
  }

  const parsed = new Date(trimmed);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toISOString().split('T')[0];
  }

  return trimmed;
};

export const getDiningReservationRows = (
  item: CartItem,
  itinerary: ItineraryResponse | null,
  options?: {
    reservationLabel?: string;
    dayLabel?: string;
    dayAtSeaLabel?: string;
  },
  specialityRestaurantsFromCf?: GetSpecialtyRestaurantBundleResponse[]
): { label: string; rows: string[] } | null => {
  const diningSelection = item.diningSelection;

  const normalizedReservations =
    diningSelection?.reservations
      ?.map((reservation) => ({
        day: normalizeDate(reservation.day),
        hour: reservation.hour?.trim() ?? '',
        restaurantName:
          specialityRestaurantsFromCf?.find(
            (restCF) => restCF.roomCode === reservation.restaurantCode
          )?.restaurantName ||
          reservation.restaurantName ||
          '',
      }))
      .filter((reservation) => Boolean(reservation.day && reservation.hour)) ?? [];

  const fallbackDate = normalizeDate(diningSelection?.date);
  const fallbackTime = diningSelection?.time?.trim() ?? '';

  let effectiveReservations: { day: string; hour: string; restaurantName: string }[];
  if (normalizedReservations.length > 0) {
    effectiveReservations = normalizedReservations;
  } else if (fallbackDate && fallbackTime) {
    effectiveReservations = [
      {
        day: fallbackDate,
        hour: fallbackTime,
        restaurantName: diningSelection?.restaurantName ?? '',
      },
    ];
  } else {
    effectiveReservations = [];
  }

  if (
    !diningSelection ||
    diningSelection.reservationMode === 'WITHOUT_TIMESLOT' ||
    effectiveReservations.length === 0
  ) {
    return null;
  }

  const dayLabel = options?.dayLabel || 'Day';
  const dayAtSeaLabel = options?.dayAtSeaLabel || 'Day at sea';
  const reservationLabel = options?.reservationLabel || 'Reservation';
  const shouldShowRestaurantName = (diningSelection.maximumSlotsBookable ?? 1) > 1;

  const itineraryDaysWithSea =
    itinerary && itinerary.days.length > 0 ? addDaysAtSea(itinerary, dayAtSeaLabel).days : [];

  const rows = effectiveReservations.map((reservation) => {
    const matchedDayIndex =
      itineraryDaysWithSea.length > 0
        ? itineraryDaysWithSea.findIndex((day) => normalizeDate(day.date) === reservation.day)
        : -1;

    const matchedDay = matchedDayIndex >= 0 ? itineraryDaysWithSea[matchedDayIndex] : undefined;
    const dayNumber = matchedDayIndex >= 0 ? matchedDayIndex + 1 : 1;

    const isAtSea = Boolean(
      (matchedDay && 'dayAtSea' in matchedDay && matchedDay.dayAtSea) ||
        matchedDay?.port?.code === 'SEA'
    );
    const portOrSeaLabel = isAtSea ? dayAtSeaLabel : matchedDay?.port?.name || dayAtSeaLabel;

    const segments = [
      `${dayLabel} ${dayNumber}`,
      portOrSeaLabel,
      ...(shouldShowRestaurantName && reservation.restaurantName
        ? [reservation.restaurantName]
        : []),
      formatTime(reservation.hour),
    ];

    return segments.join(', ');
  });

  return {
    label: reservationLabel,
    rows,
  };
};
